<template>
  <div class="text-left convive-page">
    <BreadcrumbsNav />

    <!-- Hero -->
    <section class="hero-section">
      <v-row align="center">
        <v-col cols="12" md="7">
          <div class="hero-badge">
            Élèves · Collégiens · Lycéens · Chef de salle · Agent de restauration · Animateurs · Familles
          </div>
          <h1 class="hero-title">
            Ton avis compte.
            <br />
            <span class="hero-title--accent">Et ça change vraiment le menu !</span>
          </h1>
          <p class="hero-desc">
            Avec Labricot dans ma cantine, les enfants votent sur leurs repas, découvrent ce qui est gaspillé et
            participent à des défis pour manger mieux — le tout en s'amusant.
          </p>
          <div class="hero-pills">
            <span class="hero-pill">⚡ 2 clics pour voter</span>
            <span class="hero-pill">🎮 Défis anti-gaspi</span>
            <span class="hero-pill">🏆 Badges & récompenses</span>
          </div>
        </v-col>
        <v-col cols="12" md="5" class="d-none d-md-flex justify-center">
          <v-img src="/static/images/doodles-dsfr/secondary/DogDoodle.png" contain max-width="220" />
        </v-col>
      </v-row>
    </section>

    <!-- Vote enfant -->
    <section class="section-vote">
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="feature-label feature-label--purple">Pour les élèves</div>
          <h2 class="section-title">Voter sur le repas du jour — en 2 clics</h2>
          <p class="feature-desc">
            Sur la tablette à la sortie de la cantine, chaque enfant peut donner son avis en quelques secondes. Pas de
            lecture, pas de formulaire — juste des emojis et des images.
          </p>
          <div class="vote-steps">
            <div class="vote-step" v-for="step in voteSteps" :key="step.num">
              <div class="vote-step__num">{{ step.num }}</div>
              <div class="vote-step__text">
                <strong>{{ step.title }}</strong>
                <span>{{ step.desc }}</span>
              </div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <!-- Simulation de l'interface de vote -->
          <div class="vote-mockup">
            <div class="vote-mockup__screen">
              <div class="vote-mockup__q">
                As-tu aimé
                <br />
                le repas ?
              </div>
              <div class="vote-mockup__choices">
                <button
                  class="vote-choice vote-choice--yes"
                  :class="{ selected: voted === 'yes' }"
                  @click="voted = 'yes'"
                >
                  <span class="vote-choice__emoji">😄</span>
                  <span>Oui !</span>
                </button>
                <button
                  class="vote-choice vote-choice--mid"
                  :class="{ selected: voted === 'mid' }"
                  @click="voted = 'mid'"
                >
                  <span class="vote-choice__emoji">😐</span>
                  <span>Moyen</span>
                </button>
                <button class="vote-choice vote-choice--no" :class="{ selected: voted === 'no' }" @click="voted = 'no'">
                  <span class="vote-choice__emoji">😞</span>
                  <span>Non</span>
                </button>
              </div>
              <transition name="fade">
                <div class="vote-mockup__followup" v-if="voted === 'no'">
                  <div class="vote-mockup__q2">
                    Pourquoi tu n'as
                    <br />
                    pas assez mangé ?
                  </div>
                  <div class="vote-followup-list">
                    <div class="vote-followup-item">🥄 Pas assez de quantité</div>
                    <div class="vote-followup-item">😕 Je n'ai pas aimé</div>
                    <div class="vote-followup-item">❓ Autre raison</div>
                  </div>
                </div>
              </transition>
              <div class="vote-mockup__hint" v-if="!voted">Clique pour essayer →</div>
              <div class="vote-mockup__hint vote-mockup__hint--valid" v-else-if="voted !== 'no'">
                ✓ Réponse enregistrée !
              </div>
            </div>
            <div class="vote-mockup__caption">Interface tablette en cantine</div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Labrigame -->
    <section class="section-labrigame">
      <div class="labrigame-banner">
        <div class="labrigame-banner__left">
          <div class="feature-label feature-label--game">Labrigame</div>
          <h2 class="section-title" style="color: white;">Le jeu anti-gaspillage qui change les comportements</h2>
          <p class="feature-desc" style="color: rgba(255,255,255,0.8);">
            Labrigame transforme la lutte contre le gaspillage en aventure collective. Les enfants accumulent des
            points, débloquent des badges et participent à des batailles entre cantines — tout en apprenant à mieux
            manger.
          </p>
          <div class="game-features">
            <div class="game-feature" v-for="f in gameFeatures" :key="f.title">
              <span class="game-feature__icon">{{ f.icon }}</span>
              <div>
                <strong class="game-feature__title">{{ f.title }}</strong>
                <span class="game-feature__desc">{{ f.desc }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="labrigame-banner__right">
          <div class="game-mockup">
            <div class="game-mockup__live">🔴 BATTLE EN DIRECT !</div>
            <div class="game-mockup__battle">Collège Victor Hugo vs Collège Jean Moulin</div>
            <div class="game-mockup__timer">2j 14h</div>
            <div class="game-mockup__progress">
              <div class="progress-section">
                <div class="progress-label">Progression Globale</div>
                <div class="progress-bar-track">
                  <div class="progress-bar-fill" style="width: 73%"></div>
                </div>
                <div class="progress-val">1850 / 2500 XP</div>
              </div>
            </div>
            <div class="game-badges">
              <div class="game-badge" v-for="b in badges" :key="b.label">
                <div class="game-badge__icon">{{ b.icon }}</div>
                <div class="game-badge__label">{{ b.label }}</div>
                <div class="game-badge__locked" v-if="b.locked">🔒</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sensibilisation au gaspillage -->
    <section class="section-gaspi">
      <v-row align="center">
        <v-col cols="12" md="6">
          <div class="feature-label feature-label--orange">Pour les animateurs</div>
          <h2 class="section-title">Montrer le gaspillage — pour qu'il devienne réel</h2>
          <p class="feature-desc">
            L'animateur affiche en temps réel ce qui a été jeté à chaque table. Pas des chiffres abstraits : des
            équivalences concrètes, visuelles, qui parlent aux enfants.
          </p>
          <ul class="gaspi-list">
            <li v-for="item in gaspiItems" :key="item">{{ item }}</li>
          </ul>
        </v-col>
        <v-col cols="12" md="6">
          <div class="gaspi-mockup">
            <div class="gaspi-mockup__title">♻️ Gaspillage de ce service</div>
            <div class="gaspi-mockup__big">3,2 kg</div>
            <div class="gaspi-mockup__sub">jetés aujourd'hui à votre table</div>
            <div class="gaspi-equiv">
              <div class="gaspi-equiv__item" v-for="eq in equivalences" :key="eq.label">
                <span class="gaspi-equiv__icon">{{ eq.icon }}</span>
                <span>{{ eq.label }}</span>
              </div>
            </div>
            <div class="gaspi-challenge">
              <span>🏆</span>
              <span>Défi de la semaine : passer sous 2 kg — vous y êtes presque !</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- Collégiens / Lycéens -->
    <section class="section-colleges">
      <div class="college-banner">
        <h2 class="section-title" style="margin-bottom: 8px;">Pour les collégiens et lycéens</h2>
        <p class="section-subtitle">
          Des modules d'enseignement ludiques sur le bien-manger, en format Moodle gamifié.
        </p>
        <v-row class="mt-6">
          <v-col cols="12" sm="6" md="3" v-for="module in collegeModules" :key="module.title">
            <div class="college-card">
              <div class="college-card__icon">{{ module.icon }}</div>
              <div class="college-card__title">{{ module.title }}</div>
              <div class="college-card__desc">{{ module.desc }}</div>
            </div>
          </v-col>
        </v-row>
      </div>
    </section>

    <!-- CTA -->
    <section class="section-cta">
      <h2 class="cta-title">Envie d'engager vos convives dans la démarche ?</h2>
      <p class="cta-desc">Demandez une démo de Labrigame et du système de vote pour votre établissement.</p>
      <div class="cta-actions">
        <v-btn color="primary" large class="cta-btn mr-4" href="mailto:contact@labsoft.fr">
          Demander une démo
        </v-btn>
        <v-btn outlined large class="cta-btn" :to="{ name: 'GestionnairePage2' }">
          Espace Gestionnaire →
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import BreadcrumbsNav from "@/components/BreadcrumbsNav"

export default {
  name: "ConvivePage",
  components: { BreadcrumbsNav },
  data() {
    return {
      voted: null,
      voteSteps: [
        {
          num: "1",
          title: "As-tu aimé le repas ?",
          desc: "Oui, Moyen ou Non — un seul tap suffit.",
        },
        {
          num: "2",
          title: "As-tu assez mangé ?",
          desc: "Si non, l'enfant précise la raison (quantité, goût, autre).",
        },
        {
          num: "3",
          title: "Résultats transmis en temps réel",
          desc: "L'animateur et la cuisine centrale reçoivent les données instantanément.",
        },
      ],
      gameFeatures: [
        {
          icon: "⚔️",
          title: "Battles Resto",
          desc: "Challenges collectifs inter-cantines en temps limité",
        },
        {
          icon: "🏅",
          title: "Badges & Certifications",
          desc: "NutriPasseport Bronze, Argent, Or selon la progression",
        },
        {
          icon: "📋",
          title: "Missions terrain",
          desc: "Défis concrets à réaliser pendant le service",
        },
        {
          icon: "📊",
          title: "Classement réseau",
          desc: "Visualisation de sa position dans le réseau de cantines",
        },
      ],
      badges: [
        { icon: "🌱", label: "Éco-Starter", locked: false },
        { icon: "♻️", label: "Anti-Gaspi", locked: false },
        { icon: "🥗", label: "Nutri-Pro", locked: true },
      ],
      gaspiItems: [
        "🥄 Affichage du gaspillage en kg par table et par service",
        "📈 Évolution semaine par semaine visible par tous",
        "🎯 Défis collectifs pour réduire les quantités jetées",
        "🔔 Alertes ludiques quand l'objectif est atteint",
      ],
      equivalences: [
        { icon: "🍔", label: "= 8 hamburgers" },
        { icon: "💧", label: "= 1200 L d'eau" },
        { icon: "🚗", label: "= 4 km en voiture" },
      ],
      collegeModules: [
        {
          icon: "📚",
          title: "Apprentissage gamifié",
          desc: "Modules interactifs sur la nutrition, l'alimentation et le gaspillage",
        },
        {
          icon: "✅",
          title: "Validation des compétences",
          desc: "Enquêtes intelligentes et certifications progressives",
        },
        {
          icon: "📅",
          title: "Check-in hebdomadaire",
          desc: "Partage de son menu idéal de manière anonyme",
        },
        {
          icon: "🤝",
          title: "Intelligence collective",
          desc: "Participation à la réduction du gaspillage à l'échelle du réseau",
        },
      ],
    }
  },
}
</script>

<style scoped>
.convive-page {
  font-family: "Marianne", "Segoe UI", sans-serif;
}

/* Hero */
.hero-section {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9ff 60%, #e4dfff 100%);
  border-radius: 20px;
  padding: 48px 40px;
  margin-bottom: 56px;
  border: 1px solid #c4b8f5;
}

.hero-badge {
  display: inline-block;
  background: #e4dfff;
  color: #5539cc;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 20px;
}

.hero-title {
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 900;
  line-height: 1.25;
  color: #1a1a2e;
  margin-bottom: 16px;
}

.hero-title--accent {
  color: #6c4de0;
}

.hero-desc {
  font-size: 1rem;
  line-height: 1.7;
  color: #444;
  max-width: 520px;
  margin-bottom: 24px;
}

.hero-pills {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.hero-pill {
  background: white;
  border: 1px solid #c4b8f5;
  color: #5539cc;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
}

/* Section titles */
.section-title {
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #666;
}

/* Feature labels */
.feature-label {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 4px;
  margin-bottom: 12px;
}

.feature-label--purple {
  background: #e4dfff;
  color: #5539cc;
}

.feature-label--game {
  background: #ffd700;
  color: #1a1a2e;
}

.feature-label--orange {
  background: #fde8da;
  color: #c94b1a;
}

.feature-desc {
  font-size: 0.95rem;
  color: #555;
  line-height: 1.7;
  margin-bottom: 20px;
}

/* Vote section */
.section-vote {
  margin-bottom: 64px;
}

.vote-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.vote-step {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.vote-step__num {
  width: 32px;
  height: 32px;
  background: #6c4de0;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.vote-step__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.875rem;
  color: #444;
  padding-top: 5px;
}

.vote-step__text strong {
  color: #1a1a2e;
}

/* Vote mockup */
.vote-mockup {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.vote-mockup__screen {
  background: #4c3bce;
  border-radius: 20px;
  padding: 32px 24px;
  width: 100%;
  max-width: 340px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(76, 59, 206, 0.3);
}

.vote-mockup__q {
  font-weight: 900;
  font-size: 1.2rem;
  color: white;
  margin-bottom: 24px;
  line-height: 1.3;
}

.vote-mockup__choices {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
}

.vote-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1a1a2e;
}

.vote-choice__emoji {
  font-size: 1.8rem;
}

.vote-choice--yes {
  background: #b8f0d0;
}
.vote-choice--yes:hover,
.vote-choice--yes.selected {
  background: #22c55e;
  color: white;
  border-color: white;
  transform: scale(1.05);
}

.vote-choice--mid {
  background: #fde68a;
}
.vote-choice--mid:hover,
.vote-choice--mid.selected {
  background: #f59e0b;
  color: white;
  border-color: white;
  transform: scale(1.05);
}

.vote-choice--no {
  background: #fecaca;
}
.vote-choice--no:hover,
.vote-choice--no.selected {
  background: #ef4444;
  color: white;
  border-color: white;
  transform: scale(1.05);
}

.vote-mockup__followup {
  margin-top: 8px;
}

.vote-mockup__q2 {
  font-weight: 700;
  font-size: 0.95rem;
  color: white;
  margin-bottom: 12px;
  line-height: 1.4;
}

.vote-followup-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.vote-followup-item {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-radius: 10px;
  padding: 10px 14px;
  text-align: left;
  font-size: 0.84rem;
  font-weight: 600;
}

.vote-mockup__hint {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 8px;
}

.vote-mockup__hint--valid {
  color: #86efac;
  font-weight: 700;
}

.vote-mockup__caption {
  font-size: 0.75rem;
  color: #999;
  margin-top: 12px;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Labrigame */
.section-labrigame {
  margin-bottom: 64px;
}

.labrigame-banner {
  background: linear-gradient(135deg, #1a1a2e 0%, #2d1b6b 50%, #1a3a5c 100%);
  border-radius: 24px;
  padding: 48px 40px;
  display: flex;
  gap: 40px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.labrigame-banner__left {
  flex: 1 1 300px;
}

.labrigame-banner__right {
  flex: 0 0 280px;
}

.game-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.game-feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.game-feature__icon {
  font-size: 1.3rem;
  flex-shrink: 0;
}

.game-feature__title {
  display: block;
  color: white;
  font-size: 0.875rem;
}

.game-feature__desc {
  display: block;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.78rem;
}

/* Game mockup */
.game-mockup {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 20px;
}

.game-mockup__live {
  font-size: 0.72rem;
  font-weight: 800;
  color: #ff4444;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.game-mockup__battle {
  font-size: 0.84rem;
  color: white;
  font-weight: 700;
  margin-bottom: 4px;
}

.game-mockup__timer {
  font-size: 1.4rem;
  font-weight: 900;
  color: #ffd700;
  margin-bottom: 16px;
}

.progress-label {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
}

.progress-bar-track {
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c4de0, #a78bfa);
  border-radius: 5px;
}

.progress-val {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 16px;
}

.game-badges {
  display: flex;
  gap: 10px;
  justify-content: space-around;
}

.game-badge {
  text-align: center;
  position: relative;
}

.game-badge__icon {
  font-size: 1.8rem;
}

.game-badge__label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.6);
  display: block;
}

.game-badge__locked {
  position: absolute;
  top: -4px;
  right: -4px;
  font-size: 0.8rem;
}

/* Gaspi section */
.section-gaspi {
  margin-bottom: 64px;
}

.gaspi-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gaspi-list li {
  background: #fdf6f3;
  border-left: 3px solid #e05a2b;
  padding: 10px 16px;
  border-radius: 0 8px 8px 0;
  font-size: 0.875rem;
  color: #444;
}

.gaspi-mockup {
  background: #1a1a2e;
  border-radius: 20px;
  padding: 32px;
  color: white;
  text-align: center;
}

.gaspi-mockup__title {
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.gaspi-mockup__big {
  font-size: 3.5rem;
  font-weight: 900;
  color: #ef4444;
  line-height: 1;
  margin-bottom: 4px;
}

.gaspi-mockup__sub {
  font-size: 0.84rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 20px;
}

.gaspi-equiv {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.gaspi-equiv__item {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 0.78rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.gaspi-equiv__icon {
  font-size: 1.4rem;
}

.gaspi-challenge {
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 10px;
  padding: 12px;
  font-size: 0.8rem;
  color: #ffd700;
  display: flex;
  gap: 8px;
  text-align: left;
}

/* College section */
.section-colleges {
  margin-bottom: 64px;
}

.college-banner {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9ff 100%);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid #c4b8f5;
}

.college-card {
  background: white;
  border: 1px solid #e4dfff;
  border-radius: 14px;
  padding: 22px;
  height: 100%;
  text-align: center;
}

.college-card__icon {
  font-size: 2rem;
  margin-bottom: 10px;
}

.college-card__title {
  font-weight: 800;
  font-size: 0.9rem;
  color: #1a1a2e;
  margin-bottom: 6px;
}

.college-card__desc {
  font-size: 0.8rem;
  color: #777;
  line-height: 1.5;
}

/* CTA */
.section-cta {
  background: linear-gradient(135deg, #2d1b6b 0%, #4c3bce 100%);
  border-radius: 20px;
  padding: 56px 40px;
  text-align: center;
  margin-bottom: 40px;
}

.cta-title {
  font-size: clamp(1.4rem, 3vw, 1.9rem);
  font-weight: 900;
  color: white;
  margin-bottom: 12px;
}

.cta-desc {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1rem;
  margin-bottom: 28px;
}

.cta-btn {
  text-transform: none;
  font-weight: 700;
  border-radius: 10px;
}

@media (max-width: 768px) {
  .labrigame-banner {
    flex-direction: column;
    padding: 32px 24px;
  }
  .labrigame-banner__right {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
